import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { TaskAnalysesRevisedModel } from '../../models/task-analyses-revised.model';
import { UtilsService } from '../../shared/utils.service';
import { AppService } from '../../app.service';
import { ApiRequestService } from '../../shared/api-request.service';
import { ActivatedRoute, Router } from '@angular/router';
import { RBACService } from '../../shared/rbac.service';

@Component({
  selector: 'app-task-analyses-revised-tabs',
  templateUrl: './task-analyses-revised-tabs.component.html',
  styleUrls: ['./task-analyses-revised-tabs.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class TaskAnalysesRevisedTabsComponent implements OnInit {
  ta_revised: TaskAnalysesRevisedModel = {} as TaskAnalysesRevisedModel;
  path: string;
  urlPath: string;

  // Prevents child components from rendering until permission check passes.
  hasAccess: boolean = false;

  // RBAC permission flags. Resolved once in ngOnInit() so the template does not
  // re-evaluate permission checks on every change detection cycle.
  canEdit: boolean = false;
  canDelete: boolean = false;

  constructor(
    public utils: UtilsService,
    public app: AppService,
    private api: ApiRequestService,
    private route: ActivatedRoute,
    public router: Router,
    public rbac: RBACService
  ) {}

  ngOnInit() {
    // Resolve the RBAC permission flags used by the template before any early return.
    this.canEdit = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_TEMPLATES_TA_JSA_SWMS,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );
    this.canDelete = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_TEMPLATES_TA_JSA_SWMS,
          RBACService.RBAC_A.A_DELETE
        )
      ],
      true
    );

    // get the ta revised id from the parent route params
    this.ta_revised.id = Number(
      this.route.parent.snapshot.children[0].params.ta_revised_id
    );

    this.urlPath = this.route.snapshot.url[0].path;

    if (!this.ta_revised.id) {
      this.redirectBack();
      return;
    }

    // If user has NO TA/JSA/SWMS permissions at all, redirect back to the list.
    if (
      !this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_TEMPLATES_TA_JSA_SWMS,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_TEMPLATES_TA_JSA_SWMS,
          RBACService.RBAC_A.A_CREATE
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_TEMPLATES_TA_JSA_SWMS,
          RBACService.RBAC_A.A_EDIT
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_TEMPLATES_TA_JSA_SWMS,
          RBACService.RBAC_A.A_DELETE
        )
      ])
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to access TA/JSA/SWMS templates.'
      );
      this.redirectBack();
      return;
    }

    // If user navigates directly to the edit URL without edit permission, redirect to view.
    if (
      this.urlPath === 'edit' &&
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_TEMPLATES_TA_JSA_SWMS,
            RBACService.RBAC_A.A_EDIT
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to edit TA/JSA/SWMS templates.'
      );
      this.router.navigate([
        `/task-analyses-revised/${this.ta_revised.id}/view`
      ]);
      return;
    }

    if (this.ta_revised.id) {
      this.hasAccess = true;
      this.find(this.ta_revised.id);
    }
  }

  private find(ta_revised_id: number) {
    this.api
      .makeRequest('get', `v2/task-analyses-revised/${ta_revised_id}`, [], {})
      .then((response) => {
        this.ta_revised = response;
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  public onRemove() {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_TEMPLATES_TA_JSA_SWMS,
            RBACService.RBAC_A.A_DELETE
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete TA/JSA/SWMS templates.'
      );
      return;
    }
    this.utils.showModal(
      'Remove Template',
      `Are you sure you want to remove the "${
        this.ta_revised.task_name
      }" Template?`,
      () => {
        this.remove([this.ta_revised.id]);
      }
    );
  }

  private remove(ids: number[]) {
    this.api
      .makeRequest(
        'delete',
        `v2/task-analyses-revised/${ids.join(',')}`,
        [],
        {}
      )
      .then((response) => {
        this.utils.showToast('The Templates have been removed.');
        this.redirectBack();
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  private redirectBack() {
    this.router.navigate([`/task-analyses-revised/`]);
  }

  protected readonly RBACService = RBACService;
}
